import { useState, useEffect, useRef } from 'react';
import {
  RefreshCw,
  Loader2,
  Info,
  Plus,
  Trash2,
  Type,
  MessageSquare,
  Heart,
  Mail,
  Globe,
  Check,
  Copy,
  AlertTriangle,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import type { WizardState } from './useWizardState';

interface StepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}

const SOCIAL_PLATFORMS = ['Twitter', 'Facebook', 'Instagram', 'LinkedIn', 'YouTube'] as const;

const ICON_OPTIONS = [
  { value: 'BookOpen', label: '📖 Book / Learning' },
  { value: 'GraduationCap', label: '🎓 Graduation / Education' },
  { value: 'Award', label: '🏆 Award / Certificate' },
  { value: 'Shield', label: '🛡️ Shield / Security' },
  { value: 'Clock', label: '⏰ Clock / Time' },
  { value: 'Zap', label: '⚡ Zap / Speed' },
  { value: 'Heart', label: '❤️ Heart / Care' },
  { value: 'Star', label: '⭐ Star / Quality' },
  { value: 'Target', label: '🎯 Target / Goals' },
  { value: 'TrendingUp', label: '📈 Trending / Growth' },
  { value: 'CheckCircle', label: '✅ Check / Verified' },
  { value: 'Users', label: '👥 Users / Community' },
  { value: 'Gift', label: '🎁 Gift / Bonus' },
  { value: 'Headphones', label: '🎧 Headphones / Support' },
  { value: 'Globe', label: '🌐 Globe / Global' },
  { value: 'Lock', label: '🔒 Lock / Privacy' },
  { value: 'Truck', label: '🚚 Truck / Shipping' },
  { value: 'CreditCard', label: '💳 Payment / Secure' },
  { value: 'LifeBuoy', label: '🛟 Support / Help' },
  { value: 'Sparkles', label: '✨ Sparkles / Premium' },
];

function LucideIconPreview({ name }: { name: string }) {
  const pascalName = name
    .split(/[-_\s]+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join('');

  const IconComponent = (LucideIcons as any)[pascalName];

  if (!IconComponent) {
    return <div className="w-5 h-5 rounded bg-stone-200" title="Icon not found" />;
  }

  return <IconComponent className="w-5 h-5 text-text-secondary" />;
}

function InlineRegenerate({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="p-1 rounded-md hover:bg-stone-100 text-stone-400 hover:text-purple-500 transition-colors disabled:opacity-50"
      title="Regenerate with AI"
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <RefreshCw className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

export default function WizardStep4Content({ state, updateState }: StepProps) {
  const [heroLoading, setHeroLoading] = useState(false);
  const [vpLoading, setVpLoading] = useState(false);
  const [aboutLoading, setAboutLoading] = useState(false);
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null);
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [forwardToEmail, setForwardToEmail] = useState('');
  const [emailSetupLoading, setEmailSetupLoading] = useState(false);
  const [emailSetupDone, setEmailSetupDone] = useState(false);
  const [emailSetupSteps, setEmailSetupSteps] = useState<{ step: string; status: string; message?: string }[]>([]);
  const [emailSetupError, setEmailSetupError] = useState('');
  const heroFetched = useRef(false);
  const vpFetched = useRef(false);
  const aboutFetched = useRef(false);

  const context = {
    storeName: state.storeName,
    businessDescription: state.businessDescription,
    tagline: state.tagline,
  };

  const domain = state.frontendDomain
    ? state.frontendDomain.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')
    : '';

  // Fetch hero suggestions on mount
  useEffect(() => {
    if (heroFetched.current) return;
    heroFetched.current = true;
    if (!state.heroTitle) {
      fetchHeroSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch value propositions on mount
  useEffect(() => {
    if (vpFetched.current) return;
    vpFetched.current = true;
    if (!state.valuePropositions || state.valuePropositions.length === 0) {
      fetchValueProps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch about page suggestions on mount
  useEffect(() => {
    if (aboutFetched.current) return;
    aboutFetched.current = true;
    if (!state.aboutMission && !state.aboutDifferentiator && !state.aboutAudience) {
      fetchAboutSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchHeroSuggestions() {
    setHeroLoading(true);
    try {
      const res = await api.post<{
        title: string;
        subtitle: string;
        ctaText: string;
      }>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-hero',
        context,
      });
      updateState({
        heroTitle: res.title || '',
        heroSubtitle: res.subtitle || '',
        heroCTA: res.ctaText || '',
      });
    } catch {
      // Leave fields empty for manual entry
    } finally {
      setHeroLoading(false);
    }
  }

  async function regenerateHeroField(field: 'heroTitle' | 'heroSubtitle' | 'heroCTA') {
    setRegeneratingField(field);
    try {
      const res = await api.post<{
        title: string;
        subtitle: string;
        ctaText: string;
      }>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-hero',
        context,
      });
      if (field === 'heroTitle' && res.title) {
        updateState({ heroTitle: res.title });
      } else if (field === 'heroSubtitle' && res.subtitle) {
        updateState({ heroSubtitle: res.subtitle });
      } else if (field === 'heroCTA' && res.ctaText) {
        updateState({ heroCTA: res.ctaText });
      }
    } catch {
      // Silently fail
    } finally {
      setRegeneratingField(null);
    }
  }

  async function fetchAboutSuggestions() {
    setAboutLoading(true);
    try {
      const res = await api.post<{
        content?: string;
        mission?: string;
        differentiator?: string;
        audience?: string;
      }>('/admin/ai/generate/wizard-assist', {
        action: 'generate-about',
        context,
      });
      parseAndApplyAbout(res);
    } catch {
      // Leave fields empty for manual entry
    } finally {
      setAboutLoading(false);
    }
  }

  function parseAndApplyAbout(res: {
    content?: string;
    mission?: string;
    differentiator?: string;
    audience?: string;
  }) {
    if (res.mission || res.differentiator || res.audience) {
      updateState({
        aboutMission: res.mission || '',
        aboutDifferentiator: res.differentiator || '',
        aboutAudience: res.audience || '',
      });
    } else if (res.content) {
      // Try to extract sections from a single content block
      const text = res.content.replace(/<[^>]+>/g, '\n').trim();
      const sections = text.split(/\n{2,}/);
      if (sections.length >= 3) {
        updateState({
          aboutMission: sections[0]?.trim() || '',
          aboutDifferentiator: sections[1]?.trim() || '',
          aboutAudience: sections[2]?.trim() || '',
        });
      } else {
        // Can't reliably split - put it all in mission
        updateState({ aboutMission: text });
      }
    }
  }

  async function regenerateAboutField(
    field: 'aboutMission' | 'aboutDifferentiator' | 'aboutAudience',
  ) {
    const prompts: Record<string, string> = {
      aboutMission: 'Generate a compelling mission statement (2-3 sentences)',
      aboutDifferentiator: 'Describe what makes this business unique (2-3 sentences)',
      aboutAudience: 'Describe the target audience (2-3 sentences)',
    };

    setRegeneratingField(field);
    try {
      const res = await api.post<{
        content?: string;
        mission?: string;
        differentiator?: string;
        audience?: string;
      }>('/admin/ai/generate/wizard-assist', {
        action: 'generate-about',
        context: { ...context, field: prompts[field] },
      });

      // Check for direct field match first
      const fieldMap: Record<string, string> = {
        aboutMission: 'mission',
        aboutDifferentiator: 'differentiator',
        aboutAudience: 'audience',
      };
      const key = fieldMap[field] as keyof typeof res;
      if (res[key]) {
        updateState({ [field]: res[key] });
      } else if (res.content) {
        const text = res.content.replace(/<[^>]+>/g, '').trim();
        updateState({ [field]: text });
      }
    } catch {
      // Silently fail
    } finally {
      setRegeneratingField(null);
    }
  }

  async function fetchValueProps() {
    setVpLoading(true);
    try {
      const res = await api.post<any>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-value-props',
        context,
      });
      // Handle both response formats: { propositions } or { valuePropositions }
      const props = res.propositions || res.valuePropositions || res.result?.propositions || [];
      if (props.length > 0) {
        updateState({ valuePropositions: props });
      }
    } catch {
      // Fall back to empty cards
      updateState({
        valuePropositions: [
          { icon: 'Zap', title: '', description: '' },
          { icon: 'Shield', title: '', description: '' },
          { icon: 'Heart', title: '', description: '' },
          { icon: 'Star', title: '', description: '' },
        ],
      });
    } finally {
      setVpLoading(false);
    }
  }

  function updateValueProp(index: number, field: string, value: string) {
    const updated = [...(state.valuePropositions || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateState({ valuePropositions: updated });
  }

  async function regenerateValueProp(index: number) {
    setRegeneratingField(`vp-${index}`);
    try {
      const currentVp = state.valuePropositions?.[index];
      const benefitLabel = ICON_OPTIONS.find((o) => o.value === currentVp?.icon)?.label || '';
      const res = await api.post<any>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-value-props',
        context: {
          ...context,
          benefit: benefitLabel,
          existingProps: state.valuePropositions?.filter((_: any, i: number) => i !== index).map((vp: any) => vp.title),
        },
      });
      const props = res.propositions || res.valuePropositions || [];
      if (props.length > 0) {
        // Pick the first one that's different from existing
        const existing = state.valuePropositions?.map((vp: any) => vp.title.toLowerCase()) || [];
        const newProp = props.find((p: any) => !existing.includes(p.title.toLowerCase())) || props[0];
        const updated = [...(state.valuePropositions || [])];
        updated[index] = { ...updated[index], title: newProp.title || '', description: newProp.description || '' };
        updateState({ valuePropositions: updated });
      }
    } catch {
      // Silently fail
    } finally {
      setRegeneratingField(null);
    }
  }

  async function regenerateValuePropDescription(index: number) {
    setRegeneratingField(`vp-desc-${index}`);
    try {
      const currentVp = state.valuePropositions?.[index];
      const res = await api.post<any>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-value-props',
        context: {
          ...context,
          existingTitle: currentVp?.title || '',
        },
      });
      const props = res.propositions || res.valuePropositions || [];
      if (props.length > 0) {
        // Find one that matches the current title, or just use the first
        const match = props.find((p: any) =>
          p.title?.toLowerCase() === currentVp?.title?.toLowerCase()
        ) || props[0];
        updateValueProp(index, 'description', match.description || '');
      }
    } catch {
      // Silently fail
    } finally {
      setRegeneratingField(null);
    }
  }

  async function regenerateValuePropForBenefit(index: number, icon: string) {
    setRegeneratingField(`vp-${index}`);
    try {
      const benefitLabel = ICON_OPTIONS.find((o) => o.value === icon)?.label || '';
      const res = await api.post<any>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-value-props',
        context: {
          ...context,
          benefit: benefitLabel,
          existingProps: state.valuePropositions?.filter((_: any, i: number) => i !== index).map((vp: any) => vp.title),
        },
      });
      const props = res.propositions || res.valuePropositions || [];
      if (props.length > 0) {
        const newProp = props[0];
        const updated = [...(state.valuePropositions || [])];
        updated[index] = { icon, title: newProp.title || '', description: newProp.description || '' };
        updateState({ valuePropositions: updated });
      }
    } catch {
      // Silently fail — icon is already updated
    } finally {
      setRegeneratingField(null);
    }
  }

  function addSocialLink() {
    const links = [...(state.socialLinks || [])];
    links.push({ platform: 'Twitter', url: '' });
    updateState({ socialLinks: links });
  }

  function removeSocialLink(index: number) {
    const links = [...(state.socialLinks || [])];
    links.splice(index, 1);
    updateState({ socialLinks: links });
  }

  function updateSocialLink(index: number, field: 'platform' | 'url', value: string) {
    const links = [...(state.socialLinks || [])];
    links[index] = { ...links[index], [field]: value };
    updateState({ socialLinks: links });
  }

  const defaultEmail = state.frontendDomain
    ? `support@${state.frontendDomain.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')}`
    : '';

  async function handleSetupEmailRouting() {
    if (!domain) return;
    setEmailSetupLoading(true);
    setEmailSetupError('');
    try {
      // Build email pairs from the UI
      const rawPairs = emailAddresses.length > 0
        ? emailAddresses
        : [{ from: defaultEmail, to: forwardToEmail || '' }];
      // Send ALL pairs - those with "to" get created, those without get deleted
      const pairs = rawPairs
        .map((e: any) => typeof e === 'string' ? { from: e, to: forwardToEmail || '' } : e)
        .filter((p: any) => p.from);

      if (pairs.length === 0) {
        setEmailSetupError('Add at least one email address.');
        setEmailSetupLoading(false);
        return;
      }

      const res = await api.post<any>('/admin/platform/site-wizard/setup-email', {
        domain,
        emailPairs: pairs,
      });
      // Collect all steps from all results
      const allSteps = res.results?.flatMap((r: any) => r.steps || []) || [];
      setEmailSetupSteps(allSteps);

      if (res.success) {
        setEmailSetupDone(true);
        // Parse nameservers out of any step message that lists them.
        // Backend format: "Domain added! Set these nameservers at your registrar:\nns1\nns2"
        // (When the zone is already active at Cloudflare, the message says so and contains no NS list.)
        const nameservers: string[] = [];
        for (const step of allSteps) {
          const msg: string = step?.message || '';
          if (!/nameservers/i.test(msg)) continue;
          for (const line of msg.split('\n').slice(1)) {
            const trimmed = line.trim();
            if (trimmed && /\.[a-z]{2,}$/i.test(trimmed)) nameservers.push(trimmed);
          }
        }
        if (nameservers.length > 0) {
          // Fresh zone created — gate the wizard until each NS has been copied.
          updateState({ pendingNameservers: nameservers, copiedNameservers: [] });
        }
      } else {
        const failedStep = allSteps.find((s: any) => s.status === 'failed');
        setEmailSetupError(failedStep?.message || res.error || 'Setup failed.');
      }
    } catch (err: any) {
      try {
        const parsed = JSON.parse(err.message);
        setEmailSetupError(parsed.error || parsed.instructions?.join('\n') || 'Setup failed.');
      } catch {
        setEmailSetupError(err.message || 'Setup failed. Check that CLOUDFLARE_API_TOKEN is configured.');
      }
    } finally {
      setEmailSetupLoading(false);
    }
  }

  return (
    <div className="space-y-8">
      {/* ── Homepage Hero ── */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Type className="w-5 h-5" />
          Homepage Hero
        </h2>

        {heroLoading && !state.heroTitle ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-10 bg-stone-100 rounded-lg" />
            <div className="h-10 bg-stone-100 rounded-lg" />
            <div className="h-10 bg-stone-100 rounded-lg w-1/2" />
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-text-secondary mb-1">
                Hero Title
                <InlineRegenerate
                  onClick={() => regenerateHeroField('heroTitle')}
                  loading={regeneratingField === 'heroTitle'}
                />
              </label>
              <input
                type="text"
                value={state.heroTitle}
                onChange={(e) => updateState({ heroTitle: e.target.value })}
                placeholder="Your compelling headline"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-text-secondary mb-1">
                Hero Subtitle
                <InlineRegenerate
                  onClick={() => regenerateHeroField('heroSubtitle')}
                  loading={regeneratingField === 'heroSubtitle'}
                />
              </label>
              <input
                type="text"
                value={state.heroSubtitle}
                onChange={(e) => updateState({ heroSubtitle: e.target.value })}
                placeholder="Supporting text beneath the headline"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-text-secondary mb-1">
                CTA Button Text
                <InlineRegenerate
                  onClick={() => regenerateHeroField('heroCTA')}
                  loading={regeneratingField === 'heroCTA'}
                />
              </label>
              <input
                type="text"
                value={state.heroCTA}
                onChange={(e) => updateState({ heroCTA: e.target.value })}
                placeholder="Shop Now"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </section>

      {/* ── About Page ── */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          About Page
        </h2>

        {aboutLoading && !state.aboutMission ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-16 bg-stone-100 rounded-lg" />
            <div className="h-16 bg-stone-100 rounded-lg" />
            <div className="h-16 bg-stone-100 rounded-lg" />
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-text-secondary mb-1">
                What is your mission?
                <InlineRegenerate
                  onClick={() => regenerateAboutField('aboutMission')}
                  loading={regeneratingField === 'aboutMission'}
                />
              </label>
              <textarea
                rows={2}
                value={state.aboutMission}
                onChange={(e) => updateState({ aboutMission: e.target.value })}
                placeholder="Describe what drives your business..."
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-text-secondary mb-1">
                What makes you different from competitors?
                <InlineRegenerate
                  onClick={() => regenerateAboutField('aboutDifferentiator')}
                  loading={regeneratingField === 'aboutDifferentiator'}
                />
              </label>
              <textarea
                rows={2}
                value={state.aboutDifferentiator}
                onChange={(e) => updateState({ aboutDifferentiator: e.target.value })}
                placeholder="Your unique selling points..."
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-text-secondary mb-1">
                Who is your target audience?
                <InlineRegenerate
                  onClick={() => regenerateAboutField('aboutAudience')}
                  loading={regeneratingField === 'aboutAudience'}
                />
              </label>
              <textarea
                rows={2}
                value={state.aboutAudience}
                onChange={(e) => updateState({ aboutAudience: e.target.value })}
                placeholder="Describe your ideal customers..."
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 text-xs text-text-muted">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>We'll generate your About page from these answers.</p>
        </div>
      </section>

      {/* ── Value Propositions ── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Value Propositions
            <InlineRegenerate onClick={fetchValueProps} loading={vpLoading} />
          </h2>
        </div>

        <p className="text-sm text-text-muted">
          These are the 4 key benefits shown on your homepage to build trust with visitors.
          Think: why should someone choose you?
        </p>

        {vpLoading && (!state.valuePropositions || state.valuePropositions.length === 0) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-stone-100 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(state.valuePropositions || []).map((vp: any, i: number) => (
              <div
                key={i}
                className="border border-stone-200 rounded-lg p-4 space-y-3 bg-white"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <LucideIconPreview name={vp.icon} />
                  </div>
                  <select
                    value={vp.icon}
                    onChange={(e) => {
                      updateValueProp(i, 'icon', e.target.value);
                      regenerateValuePropForBenefit(i, e.target.value);
                    }}
                    className="flex-1 rounded border border-stone-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {ICON_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1">
                    Title
                    <InlineRegenerate
                      onClick={() => regenerateValueProp(i)}
                      loading={regeneratingField === `vp-${i}`}
                    />
                  </label>
                  <input
                    type="text"
                    value={vp.title}
                    onChange={(e) => updateValueProp(i, 'title', e.target.value)}
                    placeholder="e.g. Expert-Led Courses"
                    className="w-full rounded border border-stone-300 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1">
                    Description
                    <InlineRegenerate
                      onClick={() => regenerateValuePropDescription(i)}
                      loading={regeneratingField === `vp-desc-${i}`}
                    />
                  </label>
                  <input
                    type="text"
                    value={vp.description}
                    onChange={(e) => updateValueProp(i, 'description', e.target.value)}
                    placeholder="e.g. Learn from industry professionals with real-world experience"
                    className="w-full rounded border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Contact & Email ── */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Contact & Email Setup
        </h2>

        <div className="space-y-3">
          {/* Email routing pairs */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Email Forwarding
            </label>
            <p className="text-xs text-text-muted mb-3">
              Set up email addresses for your site. Each one forwards to the address you specify.
            </p>
            {state.setupAxiss && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-3">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Axiss Support Widget</p>
                  <p className="mt-1 text-blue-700">
                    Since this site uses the Axiss support widget, <code className="bg-blue-100 px-1 rounded font-mono text-xs">support@{domain}</code> will
                    be used as the sender address for customer emails and should <strong>not</strong> be forwarded.
                    Add a second email (e.g. <code className="bg-blue-100 px-1 rounded font-mono text-xs">admin@{domain}</code>) forwarded to your inbox — Axiss
                    will use it to notify you about escalations.
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {(emailAddresses.length === 0
                ? state.setupAxiss && domain
                  ? [{ from: `support@${domain}`, to: '', noForward: true }, { from: `admin@${domain}`, to: '' }]
                  : [{ from: defaultEmail, to: '' }]
                : emailAddresses.map((e) => (typeof e === 'string' ? { from: e, to: forwardToEmail } : e))
              ).map((pair: any, i: number) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
                      <input
                        type="email"
                        value={pair.from || ''}
                        onChange={(e) => {
                          const updated = [...(emailAddresses.length === 0
                            ? state.setupAxiss && domain
                              ? [{ from: `support@${domain}`, to: '', noForward: true }, { from: `admin@${domain}`, to: '' }]
                              : [{ from: defaultEmail, to: '' }]
                            : emailAddresses.map((em: any) => typeof em === 'string' ? { from: em, to: forwardToEmail } : em))];
                          updated[i] = { ...updated[i], from: e.target.value };
                          setEmailAddresses(updated as any);
                          if (i === 0) updateState({ contactEmail: e.target.value });
                        }}
                        placeholder={`support@${domain}`}
                        className="flex-1 rounded border border-stone-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center gap-2 ml-5">
                      {pair.noForward ? (
                        <span className="text-xs text-blue-600 italic">Axiss sender — not forwarded</span>
                      ) : (
                        <>
                          <span className="text-xs text-text-muted">→ forwards to</span>
                          <input
                            type="email"
                            value={pair.to || ''}
                            onChange={(e) => {
                              const updated = [...(emailAddresses.length === 0
                                ? state.setupAxiss && domain
                                  ? [{ from: `support@${domain}`, to: '', noForward: true }, { from: `admin@${domain}`, to: '' }]
                                  : [{ from: defaultEmail, to: '' }]
                                : emailAddresses.map((em: any) => typeof em === 'string' ? { from: em, to: forwardToEmail } : em))];
                              updated[i] = { ...updated[i], to: e.target.value };
                              setEmailAddresses(updated as any);
                              if (i === 0 || (!state.setupAxiss && i === 0)) setForwardToEmail(e.target.value);
                            }}
                            placeholder="your@gmail.com"
                            className="flex-1 rounded border border-stone-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  {i > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const current = emailAddresses.map((em: any) => typeof em === 'string' ? { from: em, to: forwardToEmail } : em);
                        const updated = current.filter((_: any, idx: number) => idx !== i);
                        setEmailAddresses(updated as any);
                      }}
                      className="p-2 text-red-400 hover:text-red-600 transition-colors self-start"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const current: any[] = emailAddresses.length === 0
                  ? [{ from: defaultEmail, to: forwardToEmail }]
                  : emailAddresses.map((em: any) => typeof em === 'string' ? { from: em, to: forwardToEmail } : em);
                setEmailAddresses([...current, { from: `info@${domain}`, to: '' }] as any);
              }}
              className="mt-2"
            >
              <Plus className="w-4 h-4" />
              Add Email Address
            </Button>
          </div>

          {/* Auto-setup button */}
          {domain && (
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => { setEmailSetupDone(false); handleSetupEmailRouting(); }}
                disabled={emailSetupLoading}
                className="w-full"
              >
                {emailSetupLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : emailSetupDone ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {emailSetupDone ? 'Email Routing Configured! (click to update)' : 'Setup Cloudflare Email Routing'}
              </Button>

              {emailSetupError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Setup Issue</p>
                    <p className="mt-1 text-red-700">{emailSetupError}</p>
                  </div>
                </div>
              )}

              {emailSetupDone && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 space-y-2">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <p className="font-medium">Email routing configured!</p>
                  </div>
                  {emailSetupSteps.map((step, i) => {
                    // The "Add domain to Cloudflare" step's message contains the nameservers,
                    // which we render in a dedicated card below — strip them out here.
                    const messageWithoutNs = (step.message || '')
                      .split('\n')
                      .filter((line: string) => {
                        const t = line.trim();
                        return t && !/\.[a-z]{2,}$/i.test(t);
                      })
                      .join('\n');
                    return (
                      <div key={i} className="flex items-start gap-2 ml-6">
                        <span className={step.status === 'done' ? 'text-green-600' : 'text-amber-600'}>
                          {step.status === 'done' ? '✓' : '!'}
                        </span>
                        <div>
                          <span>{step.step}</span>
                          {messageWithoutNs && (
                            <p className="text-xs text-green-700 mt-0.5 whitespace-pre-line">{messageWithoutNs}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Nameserver copy gate — blocks Continue until each NS has been copied */}
              {state.pendingNameservers.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      <p className="font-semibold">Action required: update your registrar's nameservers</p>
                      <p className="mt-1 text-amber-800">
                        Log in to your domain registrar and replace the existing nameservers with the two below.
                        You must copy <strong>each</strong> one to continue.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {state.pendingNameservers.map((ns) => {
                      const isCopied = state.copiedNameservers.includes(ns);
                      return (
                        <div
                          key={ns}
                          className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-amber-200 rounded-md"
                        >
                          <code className="text-sm font-mono text-gray-900 truncate">{ns}</code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(ns).catch(() => {});
                              if (!isCopied) {
                                updateState({
                                  copiedNameservers: [...state.copiedNameservers, ns],
                                });
                              }
                            }}
                            className={
                              'flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ' +
                              (isCopied
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-amber-100 text-amber-900 hover:bg-amber-200')
                            }
                          >
                            {isCopied ? (
                              <>
                                <Check className="w-3.5 h-3.5" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {state.copiedNameservers.length < state.pendingNameservers.length && (
                    <p className="text-xs text-amber-800">
                      Copy {state.pendingNameservers.length - state.copiedNameservers.length} more to unlock the Continue button.
                    </p>
                  )}
                </div>
              )}

              {/* Resend info */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  <span className="font-medium">Outbound Email (Resend)</span>{' '}
                  — domain verification, DNS records, and webhook registration are handled automatically when you create the site in the final step.
                </p>
              </div>
            </div>
          )}

          {/* Social Links */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Social Links
            </label>
            <div className="space-y-2">
              {(state.socialLinks || []).map((link: any, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <select
                    value={link.platform}
                    onChange={(e) => updateSocialLink(i, 'platform', e.target.value)}
                    className="rounded-lg border border-stone-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {SOCIAL_PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateSocialLink(i, 'url', e.target.value)}
                    placeholder="https://..."
                    className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => removeSocialLink(i)}
                    className="p-2 text-red-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={addSocialLink} className="mt-2">
              <Plus className="w-4 h-4" />
              Add Social Link
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
